import React, { useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, InputBase, Typography } from '@mui/material';
import { CheckCircleRounded as MatchIcon, CancelRounded as NoMatchIcon } from '@mui/icons-material';
import { GREEN, GREEN_DARK, INK, LINE, MUTED } from '../home/homeTheme';

const EXAMPLES = [
  { format: 'ABC-123 (Jira)', regex: '^[A-Z]+-\\d+$' },
  { format: '12345 (numbers only)', regex: '^\\d+$' },
  { format: 'INC0012345 (ServiceNow)', regex: '^INC\\d+$' },
  { format: 'REQ45A (letters and numbers)', regex: '^[A-Za-z0-9]+$' },
  { format: 'Exactly 5 numbers', regex: '^\\d{5}$' },
  { format: 'Between 3 and 6 letters', regex: '^[A-Za-z]{3,6}$' },
  { format: 'At most 4 digits', regex: '^\\d{1,4}$' },
  { format: 'At least 3 digits', regex: '^\\d{3,}$' },
];

const SYMBOLS = [
  ['^', 'must start with'],
  ['$', 'must end with'],
  ['[A-Z]', 'one uppercase letter'],
  ['[a-z]', 'one lowercase letter'],
  ['\\d', 'a digit (0 to 9)'],
  ['+', 'one or more of the previous character'],
  ['{n}', 'exactly n characters'],
  ['{n,m}', 'between n and m characters'],
  ['{n,}', 'at least n characters'],
];

const codeSx = { fontFamily: 'monospace', fontSize: 12.5, bgcolor: 'var(--rt-track)', color: INK, px: 0.75, py: 0.1, borderRadius: 1 };
const sectionTitleSx = { fontSize: 12, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1 };

function testRegex(regex, sample) {
  if (!regex || !sample) return null;
  try {
    return new RegExp(regex).test(sample);
  } catch (e) {
    return 'invalid';
  }
}

/**
 * Guida al campo regex del Ticket ID: esempi pronti (con "Use"), significato dei simboli
 * e una prova al volo dell'espressione su un Ticket ID.
 */
export default function RegexHelp({ open, onClose, regex, onUse }) {
  const [sample, setSample] = useState('');
  const result = testRegex(regex, sample);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 800, pb: 0.5 }}>Ticket ID regex</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13.5, color: MUTED, mb: 2 }}>
          The regex checks that Ticket IDs follow the right pattern. Pick one of the examples or adapt its letters and numbers.
        </Typography>

        <Typography sx={sectionTitleSx}>Try it</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5, flexWrap: 'wrap' }}>
          <Box component="span" sx={{ ...codeSx, py: 0.5 }}>
            {regex || 'no regex yet'}
          </Box>
          <InputBase
            value={sample}
            onChange={(e) => setSample(e.target.value)}
            placeholder="Type a Ticket ID"
            sx={{ flex: 1, minWidth: 160, height: 34, px: 1.25, fontSize: 13.5, border: '1px solid var(--rt-pill-border)', borderRadius: 2 }}
          />
          {result === true && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: GREEN_DARK, fontSize: 13, fontWeight: 700 }}>
              <MatchIcon fontSize="small" /> Valid
            </Box>
          )}
          {result === false && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'var(--rt-danger)', fontSize: 13, fontWeight: 700 }}>
              <NoMatchIcon fontSize="small" /> Not valid
            </Box>
          )}
          {result === 'invalid' && <Typography sx={{ fontSize: 13, color: 'var(--rt-danger)', fontWeight: 700 }}>The regex is not valid</Typography>}
        </Box>

        <Typography sx={sectionTitleSx}>Ready-to-use examples</Typography>
        <Box sx={{ border: `1px solid ${LINE}`, borderRadius: 2, mb: 2.5, overflow: 'hidden' }}>
          {EXAMPLES.map((ex, i) => (
            <Box key={ex.regex} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75, borderTop: i ? `1px solid ${LINE}` : 'none' }}>
              <Typography sx={{ fontSize: 13.5, color: INK, flex: 1 }}>{ex.format}</Typography>
              <Box component="span" sx={codeSx}>
                {ex.regex}
              </Box>
              {onUse && (
                <Button size="small" onClick={() => onUse(ex.regex)} sx={{ textTransform: 'none', fontWeight: 700, minWidth: 0, color: GREEN }}>
                  Use
                </Button>
              )}
            </Box>
          ))}
        </Box>

        <Typography sx={sectionTitleSx}>What the main symbols mean</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1.5, rowGap: 0.75, alignItems: 'center', mb: 2 }}>
          {SYMBOLS.map(([symbol, meaning]) => (
            <React.Fragment key={symbol}>
              <Box component="span" sx={{ ...codeSx, justifySelf: 'start' }}>
                {symbol}
              </Box>
              <Typography sx={{ fontSize: 13.5, color: INK }}>{meaning}</Typography>
            </React.Fragment>
          ))}
        </Box>
        <Typography sx={{ fontSize: 12.5, color: MUTED }}>
          For more complex patterns you can test them on regex101.com or regexr.com, or open a ticket.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="inherit" onClick={onClose} sx={{ textTransform: 'none' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
